import { useState } from 'react';
import { Upload, FileText, Loader2, ArrowRight, Sparkles, AlertCircle, Trash2, X } from 'lucide-react';
import { useAppStore } from '../store';

export default function UploadPage() {
  const { addEssayWithAI, isAnalyzing, analysisError, essays, setCurrentEssay, setActiveTab, deleteEssay, isDeletingEssay } = useAppStore();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    await addEssayWithAI(title || '未命名作文', content);
    // Only clear form if analysis succeeded (check if we switched to analysis tab)
    const { activeTab } = useAppStore.getState();
    if (activeTab === 'analysis') {
      setTitle('');
      setContent('');
    }
  };

  const handleDelete = async (essayId: string) => {
    await deleteEssay(essayId);
    setDeleteConfirmId(null);
  };

  return (
    <div className="min-h-screen bg-surface py-10 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-serif text-3xl sm:text-4xl text-primary mb-2">上传作文</h1>
          <p className="text-slate-500">把你的 TOEFL/IELTS 作文粘贴到下方，系统会自动识别并分类每一处语法错误。</p>
        </div>

        {/* Upload Form */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-10">
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">作文标题（可选）</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder='例如："托福练习 3：科技与教育"'
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">作文内容</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="把作文粘贴到这里……（建议至少 50 词）"
              rows={12}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm leading-relaxed resize-none"
            />
            <div className="flex justify-between mt-2">
              <span className="text-xs text-slate-400">
                {content.split(/\s+/).filter(Boolean).length} 词
              </span>
              <span className="text-xs text-slate-400">
                支持 TOEFL 独立/综合写作与 IELTS Task 1 / Task 2
              </span>
            </div>
          </div>

          {/* Error message */}
          {analysisError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-red-800">分析失败</div>
                <p className="text-sm text-red-600">{analysisError}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <button
              onClick={handleSubmit}
              disabled={!content.trim() || isAnalyzing}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white rounded-2xl font-semibold hover:bg-slate-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  正在分析...
                </>
              ) : (
                <>
                  <Sparkles size={20} />
                  开始分析
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
            {isAnalyzing && (
              <span className="text-sm text-slate-400">通常需要 5 到 15 秒...</span>
            )}
          </div>
        </div>

        {/* Previous Essays */}
        {essays.length > 0 && (
          <div>
            <h2 className="font-serif text-2xl text-primary mb-6">历史作文</h2>
            <div className="grid gap-4">
              {essays.map(essay => (
                <div
                  key={essay.id}
                  className="group bg-white rounded-2xl p-6 border border-slate-100 hover:border-accent/30 hover:shadow-md transition-all duration-300 relative"
                >
                  {/* Delete Confirmation Overlay */}
                  {deleteConfirmId === essay.id && (
                    <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-2xl z-10 flex items-center justify-center">
                      <div className="text-center px-6">
                        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                          <Trash2 size={20} className="text-red-500" />
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-1">确认删除？</h3>
                        <p className="text-sm text-slate-500 mb-4">"{essay.title}" 将被永久删除，此操作不可撤销。</p>
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(null); }}
                            className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                          >
                            取消
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(essay.id); }}
                            disabled={isDeletingEssay === essay.id}
                            className="px-5 py-2.5 text-sm font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                          >
                            {isDeletingEssay === essay.id ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                删除中...
                              </>
                            ) : (
                              '确认删除'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-4">
                    <button
                      onClick={() => {
                        setCurrentEssay(essay);
                        setActiveTab('analysis');
                      }}
                      className="flex items-start gap-4 text-left flex-1 min-w-0"
                    >
                      <div className="mt-1 w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <FileText size={18} className="text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-primary mb-1">{essay.title}</h3>
                        <p className="text-sm text-slate-500 line-clamp-2">{essay.content.slice(0, 120)}...</p>
                        <div className="flex gap-4 mt-2">
                          <span className="text-xs text-slate-400">
                            {new Date(essay.uploadedAt).toLocaleDateString()}
                          </span>
                          <span className="text-xs font-semibold text-red-500">
                            发现 {essay.errors.length} 处错误
                          </span>
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(essay.id);
                        }}
                        className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                        title="删除作文"
                      >
                        <Trash2 size={16} />
                      </button>
                      <Upload size={16} className="text-slate-300 group-hover:text-accent transition-colors" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
